import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import { shiftActions } from "../../sagas/shiftSlice";
import { useDispatch, useSelector } from "react-redux";
import { formatPrice } from "../../helpers/utility";

const CloseShiftModal = ({
  isOpen,
  toggle,
  currentShift,
  onCloseShift,
  isSubmitting = false,
}) => {
  const [closingCash, setClosingCash] = useState(0);
  const [closingCard, setClosingCard] = useState(0);
  const [notes, setNotes] = useState("");
  const { summary } = useSelector((state) => state.shift);
  const [numPadOpen, setNumPadOpen] = useState(false);
  const [keypadTarget, setKeypadTarget] = useState("cash");
  const dispatch = useDispatch();

  const expectedCashAmount = Number(summary?.expectedCashAmount || 0);
  const expectedCardAmount = Number(summary?.expectedCardAmount || 0);
  const totalRetours = Number(summary?.totalRetours ?? summary?.totalReturnsAmount ?? 0);
  const summaryCashDifference = Number(summary?.cashDifference || 0);
  const summaryCardDifference = Number(summary?.cardDifference || 0);

  const cashDifference = useMemo(
    () => Number(closingCash || 0) - expectedCashAmount,
    [closingCash, expectedCashAmount]
  );
  const cardDifference = useMemo(
    () => Number(closingCard || 0) - expectedCardAmount,
    [closingCard, expectedCardAmount]
  );

  useEffect(() => {
    if (!isOpen || !currentShift?.id) return;
    setClosingCash(0);
    setClosingCard(0);
    setNotes("");
    dispatch(shiftActions.fetchSummary({ shiftId: currentShift.id }));
  }, [dispatch, isOpen, currentShift?.id]);

  useEffect(() => {
    if (!isOpen || !summary) return;
    setClosingCash(Number(summary?.closingAmountCash ?? expectedCashAmount));
    setClosingCard(Number(summary?.closingAmountCard ?? expectedCardAmount));
  }, [isOpen, summary, expectedCashAmount, expectedCardAmount]);

  const preventStepChangeKeys = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  const preventWheelStepChange = (e) => {
    e.currentTarget.blur();
  };

  const handleConfirmCloseShift = () => {
    Swal.fire({
      title: "Confirmation de cloture",
      html: `
      <p>Etes-vous sur de vouloir <strong>cloturer ce shift</strong> ?</p>
      <hr/>
      <p><strong>Especes :</strong> ${closingCash}</p>
      <p><strong>Carte bancaire :</strong> ${closingCard}</p>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Oui, cloturer",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (result.isConfirmed && !isSubmitting) {
        onCloseShift({
          closingCash,
          closingCard,
          notes,
        });
      }
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Cloture du shift #{currentShift?.shiftCode}</ModalHeader>

      <ModalBody>
        <Table bordered responsive className="align-middle mb-3">
          <thead>
            <tr>
              <th >Type</th>
              <th >Montant attendu</th>
              <th >Montant cloturés</th>
              <th >Ecart</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Espece</strong></td>
              <td>{formatPrice(expectedCashAmount)}</td>
              <td>
                <Input
                  id="closingCash"
                  type="text"
                  readOnly
                  value={closingCash}
                  onKeyDown={preventStepChangeKeys}
                  onWheel={preventWheelStepChange}
                  onClick={() => {
                    setKeypadTarget("cash");
                    setNumPadOpen(true);
                  }}
                  placeholder="Saisir le montant"
                />
              </td>
              <td>
                <Badge color={cashDifference === 0 ? "success" : "danger"}>
                  {formatPrice(cashDifference)}
                </Badge>
              </td>
              
            </tr>

            <tr>
              <td><strong>Cart</strong></td>
              <td>{formatPrice(expectedCardAmount)}</td>
              <td>
                <Input
                  id="closingCard"
                  type="text"
                  readOnly
                  value={closingCard}
                  onKeyDown={preventStepChangeKeys}
                  onWheel={preventWheelStepChange}
                  onClick={() => {
                    setKeypadTarget("card");
                    setNumPadOpen(true);
                  }}
                  placeholder="Saisir le montant"
                />
              </td>
              <td>
                <Badge color={cardDifference === 0 ? "success" : "danger"}>
                  {formatPrice(cardDifference)}
                </Badge>
              </td>  
            </tr>
            <tr>
              <td><strong>Retour clients</strong></td>
              <td colSpan={3}>
                <strong className="text-danger">{formatPrice(totalRetours)}</strong>
              </td>
            </tr>
          </tbody>
        </Table>

        <FormGroup>
          <Label for="closeShiftNotes">Notes</Label>
          <Input
            id="closeShiftNotes"
            type="textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ajouter une remarque (optionnel)"
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          Annuler
        </Button>

        <Button color="danger" onClick={handleConfirmCloseShift} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Cloture en cours...
            </>
          ) : (
            "Cloturer le shift"
          )}
        </Button>
      </ModalFooter>

      <POSNumericKeypadModal
        isOpen={numPadOpen}
        toggle={() => setNumPadOpen(false)}
        title={keypadTarget === "cash" ? "Total Especes" : "Total Carte bancaire"}
        value={keypadTarget === "cash" ? closingCash : closingCard}
        allowDecimal={true}
        maxLength={10}
        onConfirm={(val) => {
          const amount = Number(val || 0);
          if (Number.isNaN(amount)) return;
          if (keypadTarget === "cash") setClosingCash(amount);
          if (keypadTarget === "card") setClosingCard(amount);
        }}
      />
    </Modal>
  );
};

export default CloseShiftModal;
